import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { List, FileText, NotebookPen, GripVertical, X } from 'lucide-react'
import useMediaQuery from '@mui/material/useMediaQuery'
import { RichTextEditor } from './RichTextEditor'
import { SaveBadge } from './SaveBadge'
import { AnalyticsButton } from './AnalyticsButton'
import { FooterActions } from './FooterActions'
import {
    savePanelState,
    getSavedPanelState,
    getDefaultPosition,
    DEFAULT_WIDTH,
    DEFAULT_HEIGHT,
} from './panelStorage'

// ─── Shared style constants ─────────────────────────────────────────

// `tw-border-solid` is required: Tailwind preflight is disabled in this repo,
// so a bare `tw-border` only sets border-width — without a style the border
// doesn't render. Remove after enabling the preflight
const AI_BUTTON_CLASS = `
    tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-3 tw-py-1.5
    tw-rounded-lg tw-border tw-border-solid tw-text-xs tw-font-medium
    tw-transition-all tw-duration-200
    tw-border-neutral-200 dark:tw-border-neutral-700/70
    tw-bg-neutral-50 dark:tw-bg-neutral-800/40
    tw-text-neutral-500 dark:tw-text-neutral-400
    hover:tw-border-[rgba(35,131,226,0.4)] dark:hover:tw-border-[rgba(35,131,226,0.3)]
    hover:tw-bg-[rgba(35,131,226,0.2)] dark:hover:tw-bg-[rgba(35,131,226,0.15)]
    hover:tw-text-[rgb(35,131,226)] dark:hover:tw-text-[rgba(35,131,226,0.9)]
`

const HEADER_BUTTON_CLASS =
    'tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-text-neutral-400 dark:tw-text-neutral-400 hover:tw-text-neutral-700 dark:hover:tw-text-neutral-200 hover:tw-bg-neutral-100 dark:hover:tw-bg-neutral-700/60 tw-transition-colors'

// ─── Main component ─────────────────────────────────────────────────

export function ProjectNotesDialog({
    open,
    onOpenChange,
    notesHtml,
    notesPlain,
    saveStatus,
    isGeneratingAnalytics,
    onEditorChange,
    onAutoGenerateAnalytics,
    onOpenAI,
    onCopy,
    onDownload,
    onGenerateGraph,
    scrollToBottomTrigger,
}) {
    const panelRef = useRef(null)
    const isMobile = useMediaQuery('(max-width:600px)')

    // Read position/size from localStorage on every open
    const saved = open ? getSavedPanelState() : null
    const defaults = open ? getDefaultPosition(saved?.width || 520) : null
    const [pos, setPos] = useState(() => getDefaultPosition())
    const [size, setSize] = useState({
        width: DEFAULT_WIDTH,
        height: undefined,
    })
    const posRef = useRef(pos)
    const sizeRef = useRef(size)
    posRef.current = pos
    sizeRef.current = size

    useEffect(() => {
        if (!open || !saved) return
        setPos({
            left: saved.left ?? defaults.left,
            top: saved.top ?? defaults.top,
        })
        setSize({
            width: saved.width || DEFAULT_WIDTH,
            height: saved.height || undefined,
        })
    }, [open])

    const wordCount = useMemo(
        () =>
            notesPlain.trim() === ''
                ? 0
                : notesPlain.trim().split(/\s+/).length,
        [notesPlain]
    )

    // Escape key closes panel — only if the event target is inside our panel
    // (avoids closing when a nested dropdown/modal handles Escape first)
    useEffect(() => {
        if (!open) return
        const onKeyDown = e => {
            if (e.key === 'Escape' && panelRef.current?.contains(e.target)) {
                onOpenChange(false)
            }
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open, onOpenChange])

    // Top-left resize handler — dragging the handle grows/shrinks the panel
    // while anchoring the bottom-right corner in place
    const handleResizePointerDown = useCallback(e => {
        e.preventDefault()
        e.stopPropagation()
        const startX = e.clientX
        const startY = e.clientY
        const startW = sizeRef.current.width || 520
        const startH =
            sizeRef.current.height || panelRef.current?.offsetHeight || 500
        const startLeft = posRef.current.left
        const startTop = posRef.current.top

        const onMove = ev => {
            const dx = startX - ev.clientX
            const dy = startY - ev.clientY
            const newW = Math.max(340, startW + dx)
            const newH = Math.max(300, startH + dy)
            const newLeft = Math.max(0, startLeft - dx)
            const newTop = Math.max(0, startTop - dy)
            setSize({ width: newW, height: newH })
            setPos({ left: newLeft, top: newTop })
        }

        const onUp = () => {
            document.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerup', onUp)
            savePanelState({
                width: Math.round(sizeRef.current.width || 520),
                height: sizeRef.current.height
                    ? Math.round(sizeRef.current.height)
                    : undefined,
                left: posRef.current.left,
                top: posRef.current.top,
            })
        }

        document.addEventListener('pointermove', onMove)
        document.addEventListener('pointerup', onUp)
    }, [])

    // Pointer-based drag — uses ref to avoid re-creating on every pos change
    const handlePointerDown = useCallback(e => {
        e.preventDefault()
        const startX = e.clientX - posRef.current.left
        const startY = e.clientY - posRef.current.top

        const onMove = ev => {
            const newLeft = Math.max(
                0,
                Math.min(ev.clientX - startX, window.innerWidth - 100)
            )
            const newTop = Math.max(
                0,
                Math.min(ev.clientY - startY, window.innerHeight - 100)
            )
            setPos({ left: newLeft, top: newTop })
        }

        const onUp = () => {
            document.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerup', onUp)
            savePanelState({
                left: posRef.current.left,
                top: posRef.current.top,
            })
        }

        document.addEventListener('pointermove', onMove)
        document.addEventListener('pointerup', onUp)
    }, [])

    return (
        <AnimatePresence>
            {open && (
                <div className="project-notes-scope">
                    {/* TODO(3d-graph-migration): z-[120000] is a temporary override
                        so the panel sits above the legacy stacking contexts (the
                        old graph/analytics layers use very high z-indexes). Revert
                        to z-[1201] — the MUI AppBar layer — once the 3D graph
                        migration lands and z-index layering is sane again. */}
                    <motion.div
                        role="dialog"
                        aria-labelledby="project-notes-title"
                        aria-modal="false"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="tw-fixed tw-z-[120000] tw-pointer-events-auto"
                        style={
                            isMobile
                                ? // Mobile: start 56px down so the panel doesn't
                                  // cover the 48px AppBar — keeps it tappable.
                                  { top: 56, left: 8, right: 8, bottom: 8 }
                                : {
                                      left: pos.left,
                                      top: pos.top,
                                      width: size.width,
                                      height: size.height || DEFAULT_HEIGHT,
                                      maxHeight: '90vh',
                                  }
                        }
                    >
                        <div
                            ref={panelRef}
                            className="
                                tw-flex tw-flex-col
                                tw-min-w-[340px] tw-max-w-[95vw]
                                tw-bg-white dark:tw-bg-neutral-900
                                tw-border tw-border-solid tw-border-neutral-200 dark:tw-border-neutral-700/80
                                tw-shadow-2xl tw-shadow-black/8 dark:tw-shadow-black/60
                                tw-rounded-xl
                                tw-overflow-visible
                                tw-h-full tw-relative
                            "
                        >
                            {/* Top-left resize handle (desktop only — touch resize is awkward and the panel is full-bleed on mobile) */}
                            {!isMobile && (
                                <div
                                    onPointerDown={handleResizePointerDown}
                                    className="tw-absolute tw-top-0 tw-left-0 tw-w-4 tw-h-4 tw-cursor-nw-resize tw-z-10"
                                    style={{ touchAction: 'none' }}
                                >
                                    <svg
                                        className="tw-w-3 tw-h-3 tw-mt-0.5 tw-ml-0.5 tw-text-neutral-300 dark:tw-text-neutral-600"
                                        viewBox="0 0 10 10"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        fill="none"
                                    >
                                        <line x1="0" y1="10" x2="10" y2="0" />
                                        <line x1="0" y1="6" x2="6" y2="0" />
                                    </svg>
                                </div>
                            )}

                            {/* ── Header: drag handle, title, save status, close ── */}
                            <div
                                onPointerDown={isMobile ? undefined : handlePointerDown}
                                className={`tw-flex-shrink-0 tw-px-3 tw-pt-2.5 tw-pb-2 tw-select-none ${
                                    isMobile
                                        ? ''
                                        : 'tw-cursor-grab active:tw-cursor-grabbing'
                                }`}
                            >
                                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                        {!isMobile && (
                                            <GripVertical className="tw-w-3.5 tw-h-3.5 tw-text-neutral-300 dark:tw-text-neutral-600 tw-flex-shrink-0" />
                                        )}
                                        <div className="tw-flex-shrink-0 tw-w-6 tw-h-6 tw-rounded-md tw-bg-neutral-100 dark:tw-bg-neutral-700/50 tw-border tw-border-solid tw-border-neutral-200 dark:tw-border-neutral-600/50 tw-flex tw-items-center tw-justify-center">
                                            <NotebookPen className="tw-w-3 tw-h-3 tw-text-neutral-600 dark:tw-text-neutral-300" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <h2
                                                id="project-notes-title"
                                                className="tw-text-sm tw-font-semibold tw-text-neutral-900 dark:tw-text-neutral-100 tw-leading-none"
                                            >
                                                Project Notes
                                            </h2>
                                            <p className="tw-text-[10px] tw-text-neutral-400 dark:tw-text-neutral-500 tw-leading-none tw-mt-0.5">
                                                Interpret graph data, save
                                                ideas, AI content, and analytics
                                                reports.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                                        <SaveBadge status={saveStatus} />
                                        <button
                                            onClick={() => onOpenChange(false)}
                                            aria-label="Close project notes"
                                            className={HEADER_BUTTON_CLASS}
                                        >
                                            <X className="tw-w-3.5 tw-h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* ── Body: analytics generator, editor, AI actions, footer ── */}
                            <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
                                <div className="tw-flex tw-flex-col tw-h-full tw-px-3 tw-pb-3">
                                    {/* Auto-generate from analytics */}
                                    <AnalyticsButton
                                        onClick={onAutoGenerateAnalytics}
                                        isLoading={isGeneratingAnalytics}
                                    />

                                    {/* Rich text editor */}
                                    <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-mb-1">
                                        <RichTextEditor
                                            value={notesHtml}
                                            onChange={onEditorChange}
                                            placeholder="Start typing your notes, or auto-generate from analytics above..."
                                            className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col"
                                            scrollToBottomTrigger={
                                                scrollToBottomTrigger
                                            }
                                        />
                                        <div className="tw-flex tw-justify-end tw-mt-1">
                                            <span className="tw-text-[10px] tw-text-neutral-400 dark:tw-text-neutral-600 tw-tabular-nums">
                                                {wordCount}{' '}
                                                {wordCount === 1
                                                    ? 'word'
                                                    : 'words'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* AI generation buttons (outline / blogpost) */}
                                    <div className="tw-flex-shrink-0 tw-space-y-1.5 tw-mb-2.5">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <span className="tw-text-[10px] tw-font-medium tw-text-neutral-400 dark:tw-text-neutral-500 tw-uppercase tw-tracking-wider">
                                                AI Generation
                                            </span>
                                            <div className="tw-flex-1 tw-h-px tw-bg-neutral-200 dark:tw-bg-neutral-800" />
                                            <span className="tw-text-[10px] tw-text-neutral-400 dark:tw-text-neutral-600">
                                                Uses notes as context
                                            </span>
                                        </div>

                                        <div className="tw-flex tw-gap-2">
                                            <motion.button
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() =>
                                                    onOpenAI('outline')
                                                }
                                                className={AI_BUTTON_CLASS}
                                            >
                                                <List className="tw-w-3.5 tw-h-3.5" />
                                                Generate Outline
                                            </motion.button>

                                            <motion.button
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() =>
                                                    onOpenAI('blogpost')
                                                }
                                                className={AI_BUTTON_CLASS}
                                            >
                                                <FileText className="tw-w-3.5 tw-h-3.5" />
                                                Generate Blogpost
                                            </motion.button>
                                        </div>
                                    </div>

                                    {/* Footer: copy, download, generate graph */}
                                    <FooterActions
                                        notesPlain={notesPlain}
                                        onCopy={onCopy}
                                        onDownload={onDownload}
                                        onGenerateGraph={onGenerateGraph}
                                    />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    )
}
